/********************************************************************************
 * Copyright (c) 2026 EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

/**
 * The panel's DOM: one text input per editable field, a status line, and the
 * document's diagnostics.
 *
 * **Host-agnostic, which is why it lives here rather than in a shell.** It
 * touches nothing but the DOM — no `vscode`, no `acquireVsCodeApi`, no Theia
 * container — so the VS Code webview and the Theia property-view widget mount
 * the same class into their own node and differ only in how they reach the
 * data head. It is the only file in this package that touches the DOM at all.
 */

import type { PropertyField, SetFieldOutcome } from '../data/order-flow-properties-model';
import {
   describeError,
   type MessageDefinition,
   renderFrameworkMessage,
   resolve,
   type ResolvedMessage,
   resolvedFromResponseError,
   type TransferDiagnostic
} from '@hydranium/protocol';
import {
   PROPERTIES_APPLY_HINT,
   PROPERTIES_DISCONNECTED,
   PROPERTIES_NO_DOCUMENT,
   PROPERTIES_NO_FIELDS,
   PROPERTIES_PREVIOUS_WRITE_CONFLICT,
   PROPERTIES_PREVIOUS_WRITE_RAN_OUT,
   PROPERTIES_PREVIOUS_WRITE_UNAVAILABLE,
   PROPERTIES_UNSAVED,
   PROPERTIES_WRITE_CONFLICT,
   PROPERTIES_WRITE_FAILED,
   PROPERTIES_WRITE_MERGED,
   PROPERTIES_WRITE_RAN_OUT,
   PROPERTIES_WRITE_UNAVAILABLE
} from './properties-messages';
import type { ResponseError } from 'vscode-jsonrpc';

/**
 * What fills a failure's `{detail}`: the sentence of an error that carries a
 * message identity, rendered through `renderMessage`, and otherwise the error's
 * technical text.
 *
 * `describeError` alone drops the identity, so an error whose sentence the host
 * can translate — a call on a session that has ended — reaches a German reader
 * as English inside a German sentence.
 */
export function describeFailure(error: unknown, renderMessage: (message: ResolvedMessage) => string): string {
   const identity = error instanceof Error ? resolvedFromResponseError(error as ResponseError<unknown>) : undefined;
   return identity ? renderMessage(identity) : describeError(error);
}

/** What the form needs from whoever owns the model. */
export interface PropertiesFormHandlers {
   /** Write one field, returning what the model made of it. */
   readonly setField: (name: string, value: string) => Promise<SetFieldOutcome>;
   /**
    * Surface a failure — the same sink the port reports through, so a failure
    * the form raises and one the transport raises reach the user by one path.
    *
    * `reported` is a complete sentence plus the identity needed to render it in
    * another language. The form resolves its own messages here rather than
    * handing over a fragment, because this tier knows neither the host's locale
    * nor whether a process hop lies between it and the surface that renders.
    */
   readonly reportError: (error: unknown, reported: ResolvedMessage) => void;
}

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/** What a host may say about how the form renders. */
export interface PropertiesFormOptions {
   /**
    * The element the document's title is drawn in.
    *
    * A host that mounts this form beneath headings of its own has to pass a
    * level below theirs, or the page's heading order jumps back up mid-page.
    * The default suits a host that gives the form a surface to itself, where
    * this heading is the first one on it.
    */
   readonly headingLevel?: HeadingLevel;
   /**
    * Turn one of this panel's messages into the text a reader sees.
    *
    * The form draws its own labels, so there is no raise site for a host to
    * resolve them at and no way for this tier to reach a locale: it runs in a
    * webview that knows none, in a page whose language is a query parameter, and
    * in a widget whose shell holds the catalogue. Omitting this is how a host
    * without i18n takes the English, which is the same fallback a missing
    * catalogue entry gives.
    */
   readonly renderMessage?: (message: ResolvedMessage) => string;
}

/** How each write outcome reads to a user. `applied` and `unchanged` are silent on purpose. */
const OUTCOME_MESSAGES: Partial<Record<SetFieldOutcome['status'], MessageDefinition<string>>> = {
   merged: PROPERTIES_WRITE_MERGED,
   conflict: PROPERTIES_WRITE_CONFLICT,
   'ran-out': PROPERTIES_WRITE_RAN_OUT,
   unavailable: PROPERTIES_WRITE_UNAVAILABLE
};

/** How a lost edit of the document shown before a switch reads, the field and the document named. */
const PREVIOUS_OUTCOME_MESSAGES: Partial<Record<SetFieldOutcome['status'], (field: string, uri: string) => ResolvedMessage>> = {
   conflict: (field, uri) => resolve(PROPERTIES_PREVIOUS_WRITE_CONFLICT, { field, uri }),
   'ran-out': (field, uri) => resolve(PROPERTIES_PREVIOUS_WRITE_RAN_OUT, { field, uri }),
   unavailable: (field, uri) => resolve(PROPERTIES_PREVIOUS_WRITE_UNAVAILABLE, { field, uri })
};

/**
 * Outcomes after which the server holds the value that was written.
 *
 * `conflict`, `ran-out` and `unavailable` are absent deliberately: the write
 * was dropped, so the box still shows something the server does not have and
 * the field stays marked. `merged` belongs here — a merge keeps both intents,
 * so this field's value did land.
 */
const SETTLED_ON_THE_WRITTEN_VALUE = new Set<SetFieldOutcome['status']>(['applied', 'unchanged', 'merged']);

/** Which outcomes colour the status line, and how. */
const OUTCOME_KINDS: Partial<Record<SetFieldOutcome['status'], string>> = {
   merged: 'merged',
   conflict: 'conflict',
   'ran-out': 'conflict',
   unavailable: 'error'
};

export class PropertiesForm {
   protected readonly heading: HTMLHeadingElement;
   /** Says the document holds text its file does not; see {@link setDirty}. */
   protected readonly unsavedNote: HTMLDivElement;
   protected readonly fieldsHost: HTMLDivElement;
   protected readonly status: HTMLDivElement;
   protected readonly diagnosticsHost: HTMLUListElement;
   /** The inputs currently on screen, by field name. */
   protected inputs = new Map<string, HTMLInputElement>();
   /**
    * Per field, the value this form last WROTE into its input.
    *
    * Not the model's current value, and the difference is what makes the
    * pending marker mean one thing. {@link setFields} deliberately never writes
    * into the input the user is typing in, so a foreign edit to that field
    * moves the model and leaves this alone — and the field is correctly NOT
    * marked, because the reader has changed nothing. Comparing against the model
    * instead would mark a field the reader never touched.
    */
   protected rendered = new Map<string, string>();
   /** Latest model values, including updates hidden by a pending draft. */
   protected modelValues = new Map<string, string>();
   /** Whether {@link rebuild} has ever run — see {@link sameNames}. */
   protected built = false;
   /** Whether a document load is in flight — see {@link setLoading}. */
   protected loading = false;
   /** Counts the documents shown, so a write answering after a switch does not mark the next one. */
   protected shown = 0;
   /** The document shown, which a lost edit of it names once another is. */
   protected documentUri = '';
   /** How this panel's own messages reach a reader — see {@link PropertiesFormOptions.renderMessage}. */
   protected readonly renderMessage: (message: ResolvedMessage) => string;

   constructor(
      root: HTMLElement,
      protected readonly handlers: PropertiesFormHandlers,
      options: PropertiesFormOptions = {}
   ) {
      this.renderMessage = options.renderMessage ?? (message => renderFrameworkMessage(message));
      this.heading = this.createElement(options.headingLevel ?? 'h1');
      this.unsavedNote = this.createElement('div');
      this.unsavedNote.className = 'unsaved';
      // A status region that stays in the document, empty while clean: a
      // screen reader announces its text changing, and often stays silent for
      // a region that appears with its text already in place.
      this.unsavedNote.setAttribute('role', 'status');
      this.fieldsHost = this.createElement('div');
      this.status = this.createElement('div');
      this.status.className = 'status';
      this.diagnosticsHost = this.createElement('ul');
      this.diagnosticsHost.className = 'diagnostics';
      root.append(this.heading, this.unsavedNote, this.fieldsHost, this.status, this.diagnosticsHost);
      this.heading.textContent = this.text(PROPERTIES_NO_DOCUMENT);
   }

   /** Whether a field holds text that has not been applied. */
   get hasDrafts(): boolean {
      return [...this.inputs.keys()].some(name => this.isPending(name));
   }

   /** Submit the drafts present at the call, retaining the marker on a refused write. */
   async applyDrafts(): Promise<boolean> {
      const shown = this.shown;
      const drafts = [...this.inputs].filter(([name]) => this.isPending(name)).map(([name, input]) => [name, input.value] as const);
      const applied = await Promise.all(drafts.map(([name, value]) => this.write(name, value)));
      return applied.every(Boolean) && shown === this.shown && !this.hasDrafts;
   }

   /** This panel's own `message`, in the reader's language. */
   protected text(message: MessageDefinition<string>): string {
      return this.renderMessage(resolve(message));
   }

   /**
    * Element creation, behind a seam so this class's decision logic can be
    * driven without a DOM.
    *
    * Every vitest project in this repository runs `environment: 'node'`, and
    * that is deliberate: a DOM shim would test a simulation of the rendering
    * that the browser e2e already proves for real, while costing a dependency
    * and a second environment. So the way to unit-test anything here is to
    * override this and return a stub — the same shape the framework's own
    * widget tests use for their overlay.
    *
    * Overriding is safe from the constructor: prototype methods resolve to the
    * subclass before its own field initializers run, so a stub must not depend
    * on subclass state.
    */
   protected createElement<K extends keyof HTMLElementTagNameMap>(tag: K): HTMLElementTagNameMap[K] {
      return document.createElement(tag);
   }

   /** The document this form is showing, for the heading. */
   setTitle(label: string | undefined): void {
      this.heading.textContent = label ?? this.text(PROPERTIES_NO_DOCUMENT);
   }

   /**
    * Show whether the document holds changes that are not on disk yet, this
    * panel's or anyone else's. The panel has no save of its own: any client
    * saves the same document, an editor or a diagram included, and the note
    * goes when one does.
    *
    * The heading carries `data-dirty` for a host to draw the editors' dirty
    * mark on; the note stays the accessible statement of it.
    */
   setDirty(dirty: boolean): void {
      this.unsavedNote.textContent = dirty ? this.text(PROPERTIES_UNSAVED) : '';
      if (dirty) {
         this.heading.setAttribute('data-dirty', '');
         this.heading.setAttribute('title', this.text(PROPERTIES_UNSAVED));
      } else {
         this.heading.removeAttribute('data-dirty');
         // Removed rather than emptied: an empty title also hides an ancestor's tooltip.
         this.heading.removeAttribute('title');
      }
   }

   /**
    * Mark a document load as in flight, so the gap between two documents is not
    * drawn as a result.
    *
    * **Switching documents legitimately passes through an empty field set.**
    * A host shows the next document's model as soon as it creates it, and the
    * model has no snapshot until its open answers. A form that renders
    * every change verbatim then flashes "this document root has no editable
    * text properties" on every switch. That message is *true* of the instant it
    * describes and *wrong* about the document the user just selected, which is
    * the worst combination: it reads as a finding rather than as a transition.
    *
    * Suppressing only the EMPTY set, and only while loading, is what keeps the
    * genuinely-empty case visible: a root that really has no string properties
    * still reports itself once the load resolves.
    */
   setLoading(loading: boolean): void {
      this.loading = loading;
   }

   /**
    * Start showing another document, or none. The next one is clean until its
    * model says otherwise. A write of the one before that answers later says
    * nothing here, and goes to the error sink when its edit was lost.
    */
   switchDocument(uri?: string): void {
      this.shown++;
      this.documentUri = uri ?? '';
      // Its inputs are drawn afresh, so an edit typed in the one before is not
      // written to it. Cleared at once with the problems of the one before,
      // since an open that fails draws neither.
      this.built = false;
      this.inputs = new Map();
      this.rendered = new Map();
      this.modelValues = new Map();
      this.fieldsHost.replaceChildren();
      this.diagnosticsHost.replaceChildren();
      // The load of the one before, if any, is abandoned. The next sets its own.
      this.loading = false;
      this.report('');
      this.setDirty(false);
   }

   /**
    * Draw `fields`.
    *
    * **Rebuilt only when the field NAMES change; otherwise values are written in
    * place, preserving focused inputs and unapplied drafts.** A properties view
    * that recreates its inputs on every model change loses the caret — and it
    * gets a model change on every foreign edit to the document, which is exactly
    * when the user is most likely to be mid-word. This is the same bug the
    * model's own echo filter exists to prevent on the write path; the filter
    * cannot help with a third party's write, so the fix has to be here too.
    *
    * An unapplied draft stays in its input after blur too. A model refresh can
    * precede a refused write's answer; replacing the draft then would erase the
    * value and its unload protection. Only an accepted write moves that draft's
    * baseline. Fields without drafts continue to follow the model when unfocused.
    * A successful submission reconciles the retained model value even while
    * focused, provided the same input still holds the submitted text. Text
    * typed since submission and inputs replaced by a rebuild stay untouched.
    */
   setFields(fields: readonly PropertyField[]): void {
      if (this.loading && fields.length === 0) {
         // A model still opening. See setLoading.
         return;
      }
      this.modelValues = new Map(fields.map(field => [field.name, field.value]));
      const names = fields.map(field => field.name);
      if (!this.sameNames(names)) {
         this.rebuild(fields);
         return;
      }
      for (const field of fields) {
         const input = this.inputs.get(field.name);
         if (input && document.activeElement !== input && !this.isPending(field.name)) {
            input.value = field.value;
            this.rendered.set(field.name, field.value);
            this.markPending(field.name);
         }
      }
   }

   /** Whether the field differs from the last value drawn or successfully applied. */
   protected isPending(name: string): boolean {
      const input = this.inputs.get(name);
      return input !== undefined && input.value !== this.rendered.get(name);
   }

   /**
    * Mark `name` when its input no longer holds what was written into it.
    *
    * The marker locates the field; the note beside it says what to do about it,
    * which is why the marker itself needs no words and so no catalogue entry in
    * any host. The note is what an input points `aria-describedby` at, generated
    * marker content being announced inconsistently and a bare asterisk saying
    * nothing useful when it is.
    */
   protected markPending(name: string): void {
      const input = this.inputs.get(name);
      const wrapper = input?.parentElement;
      if (!input || !wrapper) {
         return;
      }
      const pending = this.isPending(name);
      if (pending) {
         wrapper.setAttribute('data-pending', '');
      } else {
         wrapper.removeAttribute('data-pending');
      }
      const hint = wrapper.querySelector<HTMLElement>('.field-hint');
      if (hint) {
         hint.hidden = !pending;
      }
   }

   /**
    * Draw `diagnostics`.
    *
    * Worth showing rather than dropping, because editing a cross-reference field
    * to an unresolvable name is a legitimate outcome of this form — the transfer
    * form of a reference is its text, so the server accepts the write and reports
    * a diagnostic. Without this the write would look like it silently did
    * nothing wrong.
    *
    * `message` is shown as it arrived: the server renders it, in the locale its
    * client declared at init, so this tier neither needs a catalogue nor has
    * anything to add. Re-rendering here would be a second authority over one
    * sentence.
    */
   setDiagnostics(diagnostics: readonly TransferDiagnostic[]): void {
      this.diagnosticsHost.replaceChildren(
         ...diagnostics.map(diagnostic => {
            const item = this.createElement('li');
            item.textContent = `${diagnostic.severity}: ${diagnostic.message}`;
            return item;
         })
      );
   }

   /**
    * Say something in the status line. `kind` colours it.
    *
    * A declaration is rendered here and a plain string is shown as it stands,
    * which is what lets a caller report `describeFailure`'s output: a technical
    * error string is not translatable text and has no code to render it by.
    */
   report(message: string | MessageDefinition<string>, kind?: string): void {
      this.status.textContent = typeof message === 'string' ? message : this.text(message);
      if (kind) {
         this.status.dataset.kind = kind;
      } else {
         delete this.status.dataset.kind;
      }
   }

   /** Announce that the connection is gone and stop accepting edits. */
   setDisconnected(): void {
      for (const input of this.inputs.values()) {
         input.disabled = true;
      }
      this.report(PROPERTIES_DISCONNECTED, 'error');
   }

   /**
    * Whether the currently drawn inputs already cover exactly `names`.
    *
    * `built` is not redundant with the map size. Before the first draw the map
    * is empty, so without it a first document whose root has NO string
    * properties compares 0 against 0, takes the "nothing changed" path, and
    * leaves the panel blank instead of saying why. An empty field set is a
    * legitimate state, so it has to be drawn rather than skipped.
    */
   protected sameNames(names: readonly string[]): boolean {
      if (!this.built || names.length !== this.inputs.size) {
         return false;
      }
      return names.every(name => this.inputs.has(name));
   }

   protected rebuild(fields: readonly PropertyField[]): void {
      this.built = true;
      this.inputs = new Map();
      this.rendered = new Map();
      const shown = this.shown;
      this.fieldsHost.replaceChildren(
         ...fields.map(field => {
            const wrapper = this.createElement('div');
            wrapper.className = 'field';
            const label = this.createElement('label');
            label.textContent = field.name;
            label.htmlFor = `field-${field.name}`;
            const input = this.createElement('input');
            input.id = `field-${field.name}`;
            input.type = 'text';
            input.value = field.value;
            const hint = this.createElement('div');
            hint.id = `field-${field.name}-hint`;
            hint.className = 'field-hint';
            hint.textContent = this.text(PROPERTIES_APPLY_HINT);
            // Hidden by the ATTRIBUTE rather than by a stylesheet rule, so a
            // host that adds no CSS for this gets the behaviour rather than a
            // note that never goes away.
            hint.hidden = true;
            input.setAttribute('aria-describedby', hint.id);
            // `change`, not `input`: a field edit is a read-modify-write of the
            // WHOLE transfer root (`TransferUpdateDocumentArgs.model` IS the root — there
            // is no path-scoped variant), so writing per keystroke would send one
            // full-document update per character and reparse the file each time.
            // `change` fires on blur and on Enter, which is the granularity the
            // wire shape actually wants.
            input.addEventListener('change', () => {
               if (shown === this.shown) {
                  void this.write(field.name, input.value);
               }
            });
            // `input` DOES fire per keystroke, and may: it moves no data, only
            // the marker that says this field is holding something the server
            // has not been told about.
            input.addEventListener('input', () => this.markPending(field.name));
            wrapper.append(label, input, hint);
            this.inputs.set(field.name, input);
            this.rendered.set(field.name, field.value);
            return wrapper;
         })
      );
      if (fields.length === 0) {
         const empty = this.createElement('div');
         empty.textContent = this.text(PROPERTIES_NO_FIELDS);
         this.fieldsHost.replaceChildren(empty);
      }
   }

   protected async write(name: string, value: string): Promise<boolean> {
      const shown = this.shown;
      const uri = this.documentUri;
      const modelValues = this.modelValues;
      const input = this.inputs.get(name);
      try {
         const outcome = await this.handlers.setField(name, value);
         if (shown !== this.shown) {
            // The status line is the next document's, so a lost edit goes to the sink.
            const lost = PREVIOUS_OUTCOME_MESSAGES[outcome.status];
            if (lost) {
               this.handlers.reportError(undefined, lost(name, uri));
            }
            return false;
         }
         this.report(OUTCOME_MESSAGES[outcome.status] ?? '', OUTCOME_KINDS[outcome.status]);
         if (SETTLED_ON_THE_WRITTEN_VALUE.has(outcome.status)) {
            // A rebuild gives the replacement input its own baseline and draft.
            // The old input's response cannot acknowledge either one.
            if (!input || this.inputs.get(name) !== input) {
               return true;
            }
            // A model redraw cannot acknowledge a draft: it may precede a
            // refused response. Only a successful outcome moves its baseline.
            this.rendered.set(name, value);
            const latest = this.modelValues.get(name);
            // A model update may arrive before the response, while the draft
            // still prevents a redraw. Do not replace text typed since submission
            // or restore a snapshot from before the write began.
            if (input.value === value && this.modelValues !== modelValues && latest !== undefined) {
               input.value = latest;
               this.rendered.set(name, latest);
            }
            this.markPending(name);
            return true;
         }
      } catch (error: unknown) {
         const detail = describeFailure(error, this.renderMessage);
         this.handlers.reportError(error, resolve(PROPERTIES_WRITE_FAILED, { field: name, uri, detail }));
         if (shown === this.shown) {
            this.report(detail, 'error');
         }
      }
      return false;
   }
}
